import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  InformationCircleIcon, 
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  ArrowDownIcon,
  ChartBarIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { Disclosure } from '@headlessui/react'
import { Helmet } from 'react-helmet-async'
import React from 'react'
import { SEO, createStructuredData } from '../components/SEO'

const schema = z.object({
  homePrice: z.number().min(1, 'Home price is required'),
  downPaymentPercentage: z.number().min(0, 'Down payment percentage must be 0 or greater'),
  interestRate: z.number().min(0, 'Interest rate must be 0 or greater'),
  loanTerm: z.number().min(1, 'Loan term is required'),
  propertyTaxRate: z.number().min(0, 'Property tax rate must be 0 or greater'),
  homeInsurance: z.number().min(0, 'Home insurance must be 0 or greater'),
  pmiRate: z.number().min(0, 'PMI rate must be 0 or greater'),
  hoaFees: z.number().min(0, 'HOA fees must be 0 or greater'),
})

type FormData = z.infer<typeof schema>

interface AmortizationRow {
  month: number
  principal: number
  interest: number
  totalPayment: number
  balance: number
}

interface CalculationResults {
  monthlyPayment: number
  principalAndInterest: number
  propertyTax: number
  insurance: number
  pmi: number
  loanAmount: number
  homePrice: number
  downPayment: number
  downPaymentPercent: number
  totalInterest: number
  interestRate: number
  loanTerm: number
  amortizationSchedule: AmortizationRow[]
}

export default function MortgageCalculator() {
  const [results, setResults] = useState<CalculationResults | null>(null)
  const [formValues, setFormValues] = useState<FormData | null>(null)
  const [showAmortization, setShowAmortization] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      homePrice: 300000,
      downPaymentPercentage: 20,
      interestRate: 7.5,
      loanTerm: 30,
      propertyTaxRate: 2,
      homeInsurance: 2500,
      hoaFees: 0,
    },
    mode: 'onChange'
  })

  const homePrice = watch('homePrice')
  const downPaymentPercentage = watch('downPaymentPercentage')

  useEffect(() => {
    if (homePrice && downPaymentPercentage) {
      const downPayment = calculateDownPaymentAmount(homePrice, downPaymentPercentage)
      if (downPayment / homePrice >= 0.2) {
        setValue('pmiRate', 0)
      } else {
        setValue('pmiRate', 0.5) // Reset to default PMI rate
      }
    }
  }, [homePrice, downPaymentPercentage, setValue])

  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [results]);

  useEffect(() => {
    const initialFields = ['homePrice', 'homeInsurance', 'hoaFees'] as const;
    initialFields.forEach(field => {
      const currentValue = watch(field);
      if (currentValue !== undefined && currentValue !== null) {
        setValue(field, currentValue, { 
          shouldValidate: true,
          shouldDirty: true
        });
      }
    });
  }, [])

  const calculateMonthlyPayment = (
    principal: number,
    annualRate: number,
    years: number
  ): number => {
    const monthlyRate = annualRate / 1200 // Convert annual rate to monthly
    const numPayments = years * 12
    
    if (monthlyRate === 0) {
      return principal / numPayments
    }
    
    const x = Math.pow(1 + monthlyRate, numPayments)
    return (principal * monthlyRate * x) / (x - 1)
  }

  const calculateAmortizationSchedule = (
    loanAmount: number,
    annualRate: number,
    years: number
  ): AmortizationRow[] => {
    const monthlyRate = annualRate / 1200 // Convert annual rate to monthly (divide by 100 for percentage and 12 for months)
    const numPayments = years * 12
    const monthlyPayment = calculateMonthlyPayment(loanAmount, annualRate, years)
    
    let balance = loanAmount
    const schedule: AmortizationRow[] = []

    for (let month = 1; month <= numPayments; month++) {
      const interest = balance * monthlyRate
      const principal = monthlyPayment - interest
      balance = balance - principal

      schedule.push({
        month,
        principal: Math.round(principal * 100) / 100,
        interest: Math.round(interest * 100) / 100,
        totalPayment: Math.round(monthlyPayment * 100) / 100,
        balance: Math.round(balance * 100) / 100
      })
    }

    return schedule
  }

  const calculateMonthlyPrincipal = (
    loanAmount: number,
    annualRate: number,
    years: number,
    monthlyPayment: number
  ) => {
    const monthlyRate = annualRate / 1200;
    const numPayments = years * 12;
    const factor = monthlyRate / (1 - Math.pow(1 + monthlyRate, -numPayments));
    return monthlyPayment - (loanAmount * factor * monthlyRate / (monthlyRate + 1));
  };

  const calculateMonthlyInterest = (
    loanAmount: number,
    annualRate: number,
    years: number,
    monthlyPayment: number
  ) => {
    const monthlyRate = annualRate / 1200;
    const numPayments = years * 12;
    const factor = monthlyRate / (1 - Math.pow(1 + monthlyRate, -numPayments));
    return loanAmount * factor * monthlyRate / (monthlyRate + 1);
  };

  const onSubmit = (data: FormData) => {
    setFormValues(data)
    const downPayment = calculateDownPaymentAmount(data.homePrice, data.downPaymentPercentage)
    const loanAmount = data.homePrice - downPayment

    // Calculate monthly payments
    const monthlyPayment = calculateMonthlyPayment(
      loanAmount,
      data.interestRate,
      data.loanTerm
    )

    // Calculate monthly property tax
    const monthlyPropertyTax = (data.homePrice * (data.propertyTaxRate / 100)) / 12

    // Calculate monthly insurance
    const monthlyInsurance = data.homeInsurance / 12

    // Calculate PMI if down payment is less than 20%
    const pmiPayment = downPayment / data.homePrice < 0.2 
      ? (loanAmount * (data.pmiRate || 0.5) / 100) / 12 
      : 0

    const totalMonthlyPayment = monthlyPayment + monthlyPropertyTax + monthlyInsurance + pmiPayment + (data.hoaFees || 0)

    const amortizationSchedule = calculateAmortizationSchedule(loanAmount, data.interestRate, data.loanTerm)
    const totalInterest = amortizationSchedule.reduce((sum, row) => sum + row.interest, 0)

    setResults({
      monthlyPayment: totalMonthlyPayment,
      principalAndInterest: monthlyPayment,
      propertyTax: monthlyPropertyTax,
      insurance: monthlyInsurance,
      pmi: pmiPayment,
      loanAmount: loanAmount,
      homePrice: data.homePrice,
      downPayment: downPayment,
      downPaymentPercent: data.downPaymentPercentage,
      totalInterest: totalInterest,
      interestRate: data.interestRate,
      loanTerm: data.loanTerm,
      amortizationSchedule: amortizationSchedule
    })
  }

  // Helper function to calculate down payment amount
  const calculateDownPaymentAmount = (price: number, percentage: number) => {
    return (percentage / 100) * price
  }

  const parseCurrency = (value: unknown) => {
    // Handle different input types
    if (value === null || value === undefined) return 0;
    
    // Convert to string if not already a string
    const stringValue = String(value);
    
    // Remove dollar signs and commas, then parse
    return parseFloat(stringValue.replace(/[$,]/g, '')) || 0;
  }

  const formatCurrency = (amount: number | undefined | null) => {
    // Handle undefined or null
    if (amount === null || amount === undefined) return '$0';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <div>
      <SEO 
        title="Illinois Mortgage Payment Calculator | Home Loan Estimator"
        description="Calculate your monthly mortgage payments in Illinois. Free tool to estimate principal, interest, taxes, and insurance for home buyers."
        keywords={["mortgage calculator", "monthly payment", "Illinois home loans", "mortgage estimation"]}
        canonicalUrl="https://calculators.northsidelegal.com/mortgage-calculator"
        structuredData={{
          "@type": "Calculator",
          "name": "Mortgage Payment Calculator for Illinois Home Buyers",
          "description": "Estimate monthly mortgage payments and loan details",
          "url": "https://calculators.northsidelegal.com/mortgage-calculator",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "7321 Ridgeway Avenue",
            "addressLocality": "Skokie",
            "addressRegion": "IL",
            "postalCode": "60076",
            "addressCountry": "US"
          },
          "provider": {
            "@type": "LegalService",
            "name": "Adler & Herbach Real Estate Attorneys",
            "url": "https://calculators.northsidelegal.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "7321 Ridgeway Avenue",
              "addressLocality": "Skokie",
              "addressRegion": "IL",
              "postalCode": "60076",
              "addressCountry": "US"
            },
            "telephone": "+1 (847) 318-3770",
            "priceRange": "$750"
          }
        }}
      />

      <Helmet>
        <title>Mortgage Payment Calculator | Loan Estimation</title>
        <meta 
          name="description" 
          content="Calculate monthly mortgage payments, interest, and total loan costs with our easy-to-use mortgage calculator" 
        />
        <meta 
          name="keywords" 
          content="mortgage calculator, loan payment, interest estimation, monthly mortgage" 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://northsidelegal.com/mortgage-calculator" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="card">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-display font-bold text-gray-900">
                  Illinois Mortgage Calculator
                </h3>
                <p className="mt-2 text-gray-500">
                  Calculate your monthly mortgage payments and view detailed amortization schedule.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="calculator-form mt-8">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="form-group">
                  <label htmlFor="homePrice" className="form-label">
                    Home Price
                    <span className="tooltip ml-1">
                      <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content -mt-1 ml-2">
                        Enter the purchase price of the home
                      </span>
                    </span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                      $
                    </span>
                    <input
                      type="text"
                      value={formatCurrency(watch('homePrice')).replace('$', '')}
                      onChange={(e) => {
                        const value = parseCurrency(e.target.value)
                        setValue('homePrice', value, { 
                          shouldValidate: true,
                          shouldDirty: true
                        })
                      }}
                      className="input-field pl-7"
                      {...register('homePrice', { 
                        setValueAs: (v) => parseCurrency(v)
                      })}
                    />
                  </div>
                  {errors.homePrice && (
                    <p className="form-error">{errors.homePrice.message}</p>
                  )}
                </div>

                <div className="form-group mt-6">
                  <label className="form-label">
                    Down Payment
                    <span className="tooltip ml-1">
                      <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content -mt-1 ml-2">
                        The amount you plan to pay upfront
                      </span>
                    </span>
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-grow">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={downPaymentPercentage}
                        onChange={(e) => {
                          const percentage = parseInt(e.target.value, 10)
                          setValue('downPaymentPercentage', percentage, {
                            shouldValidate: true,
                            shouldDirty: true
                          })
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                    <div className="w-1/4 text-right">
                      <div className="text-sm text-gray-600">
                        {downPaymentPercentage}% 
                        <span className="ml-2 text-gray-500">
                          ({formatCurrency(calculateDownPaymentAmount(homePrice, downPaymentPercentage))})
                        </span>
                      </div>
                    </div>
                  </div>
                  {errors.downPaymentPercentage && (
                    <p className="form-error">{errors.downPaymentPercentage.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="interestRate" className="form-label">
                    Interest Rate
                    <span className="tooltip ml-1">
                      <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content -mt-1 ml-2">
                        Current average rate in Illinois: 6.5%
                      </span>
                    </span>
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type="number"
                      step="0.1"
                      {...register('interestRate', { valueAsNumber: true })}
                      className="input-field pr-8"
                      placeholder="6.5"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">
                      %
                    </div>
                  </div>
                  {errors.interestRate && (
                    <p className="form-error">{errors.interestRate.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="loanTerm" className="form-label">
                    Loan Term
                    <span className="tooltip ml-1">
                      <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content -mt-1 ml-2">
                        Length of your mortgage loan
                      </span>
                    </span>
                  </label>
                  <select
                    {...register('loanTerm', { valueAsNumber: true })}
                    className="select-field"
                  >
                    <option value={30}>30 Years</option>
                    <option value={20}>20 Years</option>
                    <option value={15}>15 Years</option>
                    <option value={10}>10 Years</option>
                  </select>
                </div>
              </div>

              <div className="mt-8 bg-gray-50 p-6 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Additional Costs</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="form-group">
                    <label htmlFor="propertyTaxRate" className="form-label">
                      Property Tax Rate
                      <span className="tooltip ml-1">
                        <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                        <span className="tooltip-content -mt-1 ml-2">
                          Illinois average: 2.27%
                        </span>
                      </span>
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="number"
                        step="0.01"
                        {...register('propertyTaxRate', { valueAsNumber: true })}
                        className="input-field pr-8"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">
                        %
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="homeInsurance" className="form-label">
                      Annual Home Insurance
                      <span className="tooltip ml-1">
                        <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                        <span className="tooltip-content -mt-1 ml-2">
                          Estimated annual homeowners insurance cost
                        </span>
                      </span>
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                        $
                      </span>
                      <input
                        type="text"
                        value={formatCurrency(watch('homeInsurance')).replace('$', '')}
                        onChange={(e) => {
                          const value = parseCurrency(e.target.value)
                          setValue('homeInsurance', value, { 
                            shouldValidate: true,
                            shouldDirty: true
                          })
                        }}
                        className="input-field pl-7"
                        {...register('homeInsurance', { 
                          setValueAs: (v) => parseCurrency(v)
                        })}
                      />
                    </div>
                    {errors.homeInsurance && (
                      <p className="form-error">{errors.homeInsurance.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="pmiRate" className="form-label">
                      PMI Rate
                      <span className="tooltip ml-1">
                        <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                        <span className="tooltip-content -mt-1 ml-2">
                          Required if down payment is less than 20%
                        </span>
                      </span>
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="number"
                        step="0.01"
                        {...register('pmiRate', { valueAsNumber: true })}
                        className="input-field pr-8"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">
                        %
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="hoaFees" className="form-label">
                      Monthly HOA Fees
                      <span className="tooltip ml-1">
                        <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                        <span className="tooltip-content -mt-1 ml-2">
                          Monthly Homeowners Association fees
                        </span>
                      </span>
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                        $
                      </span>
                      <input
                        type="text"
                        value={formatCurrency(watch('hoaFees')).replace('$', '')}
                        onChange={(e) => {
                          const value = parseCurrency(e.target.value)
                          setValue('hoaFees', value, { 
                            shouldValidate: true,
                            shouldDirty: true
                          })
                        }}
                        className="input-field pl-7"
                        {...register('hoaFees', { 
                          setValueAs: (v) => parseCurrency(v)
                        })}
                      />
                    </div>
                    {errors.hoaFees && (
                      <p className="form-error">{errors.hoaFees.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button 
                  type="submit" 
                  className="btn-primary px-8 py-3 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-50 shadow-lg w-auto"
                >
                  Calculate Mortgage Details
                </button>
              </div>
            </form>

            {results && (
              <div ref={resultsRef} className="space-y-4 mt-8">
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="text-2xl font-semibold text-brand-700 mb-2">
                    {formatCurrency(results.monthlyPayment)}
                  </div>
                  <div className="text-sm text-gray-600">Estimated Monthly Payment</div>
                </div>

                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 group transition-all duration-300 ease-in-out cursor-pointer">
                        <div className="flex items-center space-x-3 text-left">
                          <span className="font-medium text-brand-700 group-hover:text-brand-800">Principal & Interest</span>
                          <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full group-hover:bg-brand-100 hidden sm:inline-block">
                            Click to Expand
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-brand-600">{formatCurrency(results.principalAndInterest)}</span>
                          <ChevronDownIcon
                            className={`h-5 w-5 text-brand-500 transform transition-transform duration-300 ${
                              open ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">
                        <div className="space-y-2 text-left">
                          <div className="flex justify-between text-sm">
                            <span>First Month Principal</span>
                            <span>{formatCurrency(results.amortizationSchedule[0].principal)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>First Month Interest</span>
                            <span>{formatCurrency(results.amortizationSchedule[0].interest)}</span>
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                <Disclosure>
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3 bg-white rounded-lg shadow-sm hover:bg-gray-50 group transition-all duration-300 ease-in-out cursor-pointer">
                        <div className="flex items-center space-x-3 text-left">
                          <span className="font-medium text-brand-700 group-hover:text-brand-800">Property Taxes & Insurance</span>
                          <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full group-hover:bg-brand-100 hidden sm:inline-block">
                            Click to Expand
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-brand-600">{formatCurrency(results.propertyTax + results.insurance + results.pmi)}</span>
                          <ChevronDownIcon
                            className={`h-5 w-5 text-brand-500 transform transition-transform duration-300 ${
                              open ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">
                        <div className="space-y-2 text-left">
                          <div className="flex justify-between text-sm">
                            <span>Property Taxes</span>
                            <span>{formatCurrency(results.propertyTax)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Home Insurance</span>
                            <span>{formatCurrency(results.insurance)}</span>
                          </div>
                          {results.pmi > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>PMI</span>
                              <span>{formatCurrency(results.pmi)}</span>
                            </div>
                          )}
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Loan Amount</span>
                    <span className="font-medium">{formatCurrency(results.loanAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Down Payment</span>
                    <span className="font-medium">{formatCurrency(results.downPayment)} ({results.downPaymentPercent}%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount to be Repaid</span>
                    <span className="font-medium">{formatCurrency(results.loanAmount + results.totalInterest)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Interest</span>
                    <span className="font-medium">{formatCurrency(results.totalInterest)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Interest Rate</span>
                    <span className="font-medium">{results.interestRate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Loan Term</span>
                    <span className="font-medium">{results.loanTerm} years</span>
                  </div>
                  <button
                    onClick={() => setShowAmortization(!showAmortization)}
                    className="btn-primary px-8 py-3 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-50 shadow-lg w-auto mt-4 flex items-center justify-center"
                  >
                    <DocumentTextIcon className="h-5 w-5 mr-2" />
                    {showAmortization ? 'Hide' : 'View'} Amortization Schedule
                  </button>
                </div>

                {showAmortization && (
                  <div className="mt-6 overflow-x-auto">
                    <div className="inline-block min-w-full align-middle">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead>
                          <tr className="bg-gray-50">
                            <th scope="col" className="py-3 pl-4 pr-3 text-left text-xs font-medium text-gray-500 sm:pl-6">
                              Month
                            </th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500">
                              Principal
                            </th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500">
                              Interest
                            </th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500">
                              Total Payment
                            </th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500">
                              Balance
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {results.amortizationSchedule.map((row, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                              <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                                {row.month}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {formatCurrency(row.principal)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {formatCurrency(row.interest)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {formatCurrency(row.totalPayment)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                {formatCurrency(row.balance)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
